<template>
  <div class="products-page">
    <!-- LOADING -->
    <div v-if="loading" class="loading-overlay">
      <Loading />
    </div>

    <!-- HERO -->
    <header class="hero">
      <h1 class="title">Produtos</h1>

      <!-- Linha 1: Search + filtros + ações -->
      <div class="topbar">
        <!-- Search pill -->
        <div class="search-wrap">
          <span class="icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path
                d="M15.5 14h-.79l-.28-.27a6.471 6.471 0 0 0 1.57-4.23C15.99 6.01 13.48 3.5 10.5 3.5S5.01 6.01 5.01 9s2.51 5.5 5.49 5.5a6.471 6.471 0 0 0 4.23-1.57l.27.28v.79l4.25 4.25c.41.41 1.08.41 1.49 0 .41-.41.41-1.08 0-1.49L15.5 14Zm-5 0C8.01 14 6 11.99 6 9.5S8.01 5 10.5 5 15 7.01 15 9.5 12.99 14 10.5 14Z"
              />
            </svg>
          </span>

          <input
            v-model.trim="q"
            type="text"
            class="search"
            placeholder="Pesquisar por nome, SKU/CNP ou EAN…"
            @input="onSearchInput"
            @keyup.enter="applySearch"
          />

          <button class="search-btn" @click="applySearch">Search</button>
        </div>

        <!-- Filtros -->
        <div class="filters">
          <div class="filter">
            <div class="label">Marca</div>
            <v-select
              class="vs"
              :options="allBrands"
              v-model="selectedBrand"
              label="name"
              :clearable="true"
              placeholder="Todas"
              @input="applySearch"
            >
              <template #option="option">
                {{ option.name }}
              </template>
            </v-select>
          </div>

          <div class="filter">
            <div class="label">Stock</div>
            <select class="input" v-model="stockFilter" @change="applySearch">
              <option value="">Todos</option>
              <option value="true">Com stock</option>
              <option value="false">Sem stock</option>
            </select>
          </div>
        </div>

        <!-- Ações -->
        <div class="primary-actions">
          <button class="btn-create" @click="goToAdd">
            <span class="plus">＋</span> Adicionar Produto
          </button>
          <button class="btn-outline" v-b-modal.modal-join>🧩 Juntar</button>
        </div>
      </div>

      <!-- Linha 2: actions bar -->
      <div class="actions-bar">
        <a class="action-btn green" href="/generated-products"
          >🧠 Produtos Gerados</a
        >
        <button class="action-btn" @click="downFile">📄 Listagem</button>
        <button class="action-btn" @click="downShebinReport">
          📊 Relatório Shebin
        </button>
        <a class="action-btn" href="/products/names">🧾 Relatório de Nomes</a>
        <a class="action-btn" href="/products/validity">⏳ Ver Validades</a>
        <a class="action-btn" href="/products/stock">📦 Relatório Stock</a>
        <a class="action-btn" href="/products/brands/report"
          >🏷️ Relatório Marcas</a
        >
        <a class="action-btn" href="/products/manualPrice/report"
          >💶 Preço Manual</a
        >
        <a class="action-btn" href="/products/aerosol/report">💨 Aerossóis</a>
        <a class="action-btn" href="/products/ne-quarantine">🚫 Quarentena NE</a>
        <button class="action-btn" @click="downOCPReport">
          📊 Produtos OCP
        </button>
      </div>
    </header>

    <!-- TABLE -->
    <section class="table-card fill-remaining">
      <div class="table-head">
        <div class="table-title">Resultados</div>
        <div class="table-meta">
          <span v-if="allFilteredProducts.length">
            {{ allFilteredProducts.length }} produtos
          </span>
          <span v-else class="muted">Sem resultados (faz search)</span>
        </div>
      </div>

      <div class="table-wrap">
        <b-table
          striped
          hover
          :items="allFilteredProducts"
          :fields="fields"
          :per-page="perPage"
          :current-page="currentPage"
          small
          responsive
          thead-class="thead-soft"
          tbody-tr-class="tbody-row"
        >
          <!-- IMAGE -->
          <template #cell(image)="data">
            <div
              class="thumb-wrap"
              @click="openImage(data.item.img, data.item.name)"
              title="Clique para ampliar"
            >
              <img
                :src="data.item.img || placeholder"
                @error="onImgError"
                alt="Produto"
                class="product-thumb"
              />
              <span class="zoom-badge">🔍</span>
            </div>
          </template>

          <!-- SKU -->
          <template #cell(sku)="data">
            <router-link
              class="link"
              :to="`product/${data.item.id_backoffice}`"
            >
              {{
                Array.isArray(data.item.sku) ? data.item.sku[0] : data.item.sku
              }}
            </router-link>

            <div v-if="data.item.sku && data.item.sku.length > 1" class="sub">
              <span class="mono"
                >({{ data.item.sku.slice(1).join(', ') }})</span
              >
            </div>
          </template>

          <!-- EAN -->
          <template #cell(ean)="data">
            <div v-if="Array.isArray(data.item.ean)">
              <div v-for="e in data.item.ean" :key="e" class="mono">
                {{ e }}
              </div>
            </div>
            <div v-else class="mono">{{ data.item.ean }}</div>
          </template>

          <!-- INFO -->
          <template #cell(info)="data">
            <div class="info">
              <div><b>Peso:</b> {{ data.item.weight }} g</div>
              <div v-if="data.item.validity">
                <b>Validade:</b> {{ splitDate(data.item.validity) }}
              </div>
              <div v-else><b>Validade:</b> Sem validade</div>
            </div>
          </template>

          <!-- STOCK MAIN -->
          <template #cell(stock)="data">
            <div class="cell">
              <!-- linha 1: valor + dot (ALINHADO AO TOPO) -->
              <div class="cell-top">
                <span class="cell-value">{{ data.item.stock }}</span>
                <span
                  class="dot"
                  :class="{
                    red: data.item.stock <= 0,
                    yellow: data.item.stock > 0 && data.item.stock < 5,
                    green: data.item.stock >= 5,
                  }"
                />
              </div>

              <!-- linha 2 -->
              <div class="cell-sub mono" v-if="data.item.average_price">
                {{ data.item.average_price }}
              </div>

              <v-select
                class="vs-mini"
                :options="status"
                v-model="data.item.stock_status"
                :clearable="false"
                @input="updateStockStatus(data.item)"
              />
            </div>
          </template>

          <!-- SUPPLIERS (todos iguais para alinhar dots em cima) -->
          <template #cell(ocp_stock)="data">
            <div class="cell">
              <div class="cell-top">
                <span class="cell-value mono">{{ data.item.ocp_stock }}</span>
                <span
                  class="dot"
                  :class="{
                    red:
                      data.item.ocp_stock == 'Indisponível' ||
                      data.item.ocp_stock == 'Nao Disponivel' ||
                      data.item.ocp_stock == '0' ||
                      data.item.ocp_stock == 'Nao Comercializado',
                    green: !(
                      data.item.ocp_stock == 'Indisponível' ||
                      data.item.ocp_stock == 'Nao Disponivel' ||
                      data.item.ocp_stock == '0' ||
                      data.item.ocp_stock == 'Nao Comercializado'
                    ),
                  }"
                />
              </div>
              <div class="cell-sub mono">{{ data.item.ocp_price }} €</div>
            </div>
          </template>

          <template #cell(alvim_stock)="data">
            <div class="cell">
              <div class="cell-top">
                <span class="cell-value mono">{{ data.item.alvim_stock }}</span>
                <span
                  class="dot"
                  :class="{
                    red:
                      data.item.alvim_stock == 'Indisponível' ||
                      Number(data.item.alvim_stock) <= 0,
                    yellow:
                      Number(data.item.alvim_stock) > 0 &&
                      Number(data.item.alvim_stock) < 5,
                    green: Number(data.item.alvim_stock) >= 5,
                  }"
                />
              </div>
              <div class="cell-sub mono">{{ data.item.alvim_price }} €</div>
            </div>
          </template>

          <template #cell(gomes_stock)="data">
            <div class="cell">
              <div class="cell-top">
                <span class="cell-value mono">{{ data.item.gomes_stock }}</span>
                <span
                  class="dot"
                  :class="{
                    red:
                      data.item.gomes_stock == 'Indisponível' ||
                      Number(data.item.gomes_stock) <= 0,
                    yellow:
                      Number(data.item.gomes_stock) > 0 &&
                      Number(data.item.gomes_stock) < 5,
                    green: Number(data.item.gomes_stock) >= 5,
                  }"
                />
              </div>
              <div class="cell-sub mono">{{ data.item.gomes_price }} €</div>
            </div>
          </template>

          <template #cell(perelhal_stock)="data">
            <div class="cell">
              <div class="cell-top">
                <span class="cell-value mono">{{
                  data.item.perelhal_stock
                }}</span>
                <span
                  class="dot"
                  :class="{
                    red:
                      data.item.perelhal_stock == 'Indisponível' ||
                      Number(data.item.perelhal_stock) <= 0,
                    yellow:
                      Number(data.item.perelhal_stock) > 0 &&
                      Number(data.item.perelhal_stock) < 5,
                    green: Number(data.item.perelhal_stock) >= 5,
                  }"
                />
              </div>
              <div class="cell-sub mono">{{ data.item.perelhal_price }} €</div>
            </div>
          </template>

          <template #cell(martins_stock)="data">
            <div class="cell">
              <div class="cell-top">
                <span class="cell-value mono">{{
                  data.item.martins_stock
                }}</span>
                <span
                  class="dot"
                  :class="{
                    red:
                      data.item.martins_stock == 'Indisponível' ||
                      Number(data.item.martins_stock) <= 0,
                    yellow:
                      Number(data.item.martins_stock) > 0 &&
                      Number(data.item.martins_stock) < 5,
                    green: Number(data.item.martins_stock) >= 5,
                  }"
                />
              </div>
              <div class="cell-sub mono">{{ data.item.martins_price }} €</div>
            </div>
          </template>

          <template #cell(nova_engel_stock)="data">
            <div class="cell">
              <div class="cell-top">
                <span class="cell-value mono">{{
                  data.item.nova_engel_stock
                }}</span>
                <span
                  class="dot"
                  :class="{
                    red:
                      data.item.nova_engel_stock == 'Indisponível' ||
                      Number(data.item.nova_engel_stock) <= 0,
                    yellow:
                      Number(data.item.nova_engel_stock) > 0 &&
                      Number(data.item.nova_engel_stock) < 5,
                    green: Number(data.item.nova_engel_stock) >= 5,
                  }"
                />
              </div>
              <div class="cell-sub mono">
                {{ data.item.nova_engel_price }} €
              </div>
            </div>
          </template>

          <template #cell(symphonya_stock)="data">
            <div class="cell">
              <div class="cell-top">
                <span class="cell-value mono">{{
                  data.item.symphonya_stock
                }}</span>
                <span
                  class="dot"
                  :class="{
                    red:
                      data.item.symphonya_stock == 'Indisponível' ||
                      Number(data.item.symphonya_stock) <= 0,
                    yellow:
                      Number(data.item.symphonya_stock) > 0 &&
                      Number(data.item.symphonya_stock) < 5,
                    green: Number(data.item.symphonya_stock) >= 5,
                  }"
                />
              </div>
              <div class="cell-sub mono">{{ data.item.symphonya_price }} €</div>
            </div>
          </template>

          <template #cell(price)="data">
            <div class="cell">
              <div class="cell-top">
                <span class="cell-value mono">{{
                  data.item.price || '-'
                }}</span>
              </div>
            </div>
          </template>
        </b-table>
      </div>
    </section>

    <!-- IMAGE PREVIEW MODAL -->
    <b-modal
      id="img-preview"
      ref="imgPreview"
      hide-footer
      centered
      title="Imagem"
      size="lg"
    >
      <div class="img-modal">
        <div class="img-modal-title">{{ previewTitle }}</div>
        <img :src="previewSrc || placeholder" class="img-modal-img" />
      </div>
    </b-modal>

    <!-- MODAL JOIN -->
    <b-modal
      ref="modal_join"
      id="modal-join"
      title="Juntar Produtos"
      hide-footer
    >
      <form v-on:submit.prevent="joinProducts()">
        <div class="form-group">
          <label for="sku_original">SKU (manter):</label>
          <input
            type="text"
            class="form-control"
            id="sku_original"
            v-model="sku_original"
          />
          <br />
          <label for="sku_to_join">SKU (juntar/eliminar):</label>
          <input
            type="text"
            class="form-control"
            id="sku_to_join"
            v-model="sku_to_join"
          />
        </div>
        <div id="btnUpdateAddress">
          <button class="btn btn-primary">Juntar</button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Loading from '@/components/Loading.vue';
import exportFromJSON from 'export-from-json';
import axios from 'axios';

const debounce = (fn, wait = 250) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), wait);
  };
};

export default {
  name: 'ProductsListNewUI',
  components: { Loading },

  data() {
    return {
      perPage: 20,
      currentPage: 1,

      loading: false,
      allFilteredProducts: [],
      placeholder:
        'https://upload.wikimedia.org/wikipedia/commons/a/ac/No_image_available.svg',

      q: '',
      appliedQ: '',
      selectedBrand: null,
      stockFilter: '',

      sku_original: '',
      sku_to_join: '',

      previewSrc: '',
      previewTitle: '',

      allBrands: [],
      status: [
        'Infinity',
        'Normal',
        'Normalix',
        'Zero',
        'Discontinued',
        'MinC No',
        'MinC Low',
        'MinC Mid',
        'MinC High',
      ],

      fields: [
        {
          key: 'sku',
          label: 'SKU/CNP',
          tdClass: 'cell-td',
          thClass: 'cell-th',
        },
        {
          key: 'image',
          label: '',
          tdClass: 'cell-td',
          thClass: 'cell-th',
          thStyle: 'width:84px',
        },
        { key: 'ean', label: 'EAN', tdClass: 'cell-td', thClass: 'cell-th' },
        {
          key: 'name',
          label: 'Nome',
          tdClass: 'cell-td name-col',
          thClass: 'cell-th',
          thStyle: 'min-width:340px',
        },
        {
          key: 'stock',
          label: 'Stock',
          tdClass: 'cell-td',
          thClass: 'cell-th',
          thStyle: 'width:190px',
        },
        {
          key: 'info',
          label: 'Info',
          tdClass: 'cell-td',
          thClass: 'cell-th',
          thStyle: 'width:170px',
        },
        {
          key: 'ocp_stock',
          label: 'OCP',
          tdClass: 'cell-td supplier-col',
          thClass: 'cell-th',
        },
        {
          key: 'alvim_stock',
          label: 'Alvim',
          tdClass: 'cell-td supplier-col',
          thClass: 'cell-th',
        },
        {
          key: 'gomes_stock',
          label: 'Gomes',
          tdClass: 'cell-td supplier-col',
          thClass: 'cell-th',
        },
        {
          key: 'perelhal_stock',
          label: 'Perelhal',
          tdClass: 'cell-td supplier-col',
          thClass: 'cell-th',
        },
        {
          key: 'martins_stock',
          label: 'Martins',
          tdClass: 'cell-td supplier-col',
          thClass: 'cell-th',
        },
        {
          key: 'nova_engel_stock',
          label: 'Nova Engel',
          tdClass: 'cell-td supplier-col',
          thClass: 'cell-th',
        },
        {
          key: 'symphonya_stock',
          label: 'Symphonya',
          tdClass: 'cell-td supplier-col',
          thClass: 'cell-th',
        },
        {
          key: 'price',
          label: 'Preço',
          tdClass: 'cell-td',
          thClass: 'cell-th',
        },
      ],
    };
  },

  computed: {
    ...mapGetters(['getFilterProducts', 'getBrands']),
  },

  methods: {
    applySearch() {
      this.appliedQ = this.q;
      this.filterProducts();
    },

    onSearchInput: debounce(function () {
      this.appliedQ = this.q;
      this.filterProducts();
    }, 250),

    onImgError(e) {
      e.target.src = this.placeholder;
    },

    openImage(src, title) {
      this.previewSrc = src;
      this.previewTitle = title || 'Produto';
      this.$refs.imgPreview.show();
    },

    splitDate(date) {
      const split = String(date).split('T');
      return split[0];
    },

    goToAdd() {
      this.$router.push(`addProduct`);
    },

    async updateStockStatus(item) {
      this.loading = true;
      try {
        await this.$store.dispatch('updateStockStatus', {
          id: item.id_backoffice,
          status: item.stock_status,
        });
        this.$swal({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Stock Status Atualizado',
          showConfirmButton: false,
          timer: 1500,
        });
      } catch (err) {
        alert(err);
      } finally {
        this.loading = false;
      }
    },

    async joinProducts() {
      if (this.sku_original === '' || this.sku_to_join === '') {
        return this.$swal({
          toast: true,
          position: 'top-end',
          icon: 'error',
          title: 'Preencha os campos',
          showConfirmButton: false,
          timer: 1500,
        });
      }

      this.loading = true;
      try {
        await this.$store.dispatch('joinProducts', {
          sku_original: this.sku_original,
          sku_to_join: this.sku_to_join,
        });

        this.$swal({
          toast: true,
          position: 'top-end',
          icon: 'success',
          title: 'Produtos Juntos',
          showConfirmButton: false,
          timer: 1500,
        });

        this.sku_original = '';
        this.sku_to_join = '';
        this.$refs.modal_join.hide();
        this.filterProducts();
      } catch (err) {
        alert(err);
      } finally {
        this.loading = false;
      }
    },

    async brands() {
      try {
        await this.$store.dispatch('getAllBrands');
        this.allBrands = (this.getBrands || [])
          .slice()
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      } catch (err) {
        alert(err);
      }
    },

    async filterProducts() {
      const hasQ = !!(this.appliedQ && this.appliedQ.trim());
      const hasBrand = !!(this.selectedBrand && this.selectedBrand.id);
      const hasStock =
        this.stockFilter === 'true' || this.stockFilter === 'false';

      if (!hasQ && !hasBrand && !hasStock) {
        this.allFilteredProducts = [];
        return;
      }

      const stockBoolean =
        this.stockFilter === 'true'
          ? true
          : this.stockFilter === 'false'
          ? false
          : null;

      this.loading = true;
      try {
        await this.$store.dispatch('searchProduct', {
          search: hasQ ? this.appliedQ : null,
          brand: hasBrand ? this.selectedBrand.id : null,
          stock: stockBoolean,
        });

        this.allFilteredProducts = this.getFilterProducts || [];
        this.perPage =
          this.allFilteredProducts.length > 0
            ? this.allFilteredProducts.length
            : 20;
      } catch (err) {
        console.log(err);
        alert(err);
      } finally {
        this.loading = false;
      }
    },

    async downFile() {
      const today = new Date();
      const date_i = `${today.getFullYear()}-${
        today.getMonth() + 1
      }-${today.getDate()}`;

      const array = (this.allFilteredProducts || []).map((product) => ({
        cnp: Array.isArray(product.sku) ? product.sku[0] : product.sku,
        nome: product.name,
        stock: product.stock,
        ne_stock: product.nova_engel_stock,
      }));

      exportFromJSON({
        data: array,
        fileName: `Produtos_${date_i}`,
        exportType: exportFromJSON.types.csv,
      });
    },

    async downShebinReport() {
      const today = new Date();
      const date_i = `${today.getFullYear()}-${
        today.getMonth() + 1
      }-${today.getDate()}`;

      const response = await axios.get(
        `${this.$store.state.apiUrl}products/relatorioShebin`
      );

      exportFromJSON({
        data: response.data.data,
        fileName: `Relatorio_Shebin_${date_i}`,
        exportType: exportFromJSON.types.csv,
      });
    },
    async downOCPReport() {
      const today = new Date();
      const date_i = `${today.getFullYear()}-${
        today.getMonth() + 1
      }-${today.getDate()}`;

      const response = await axios.get(
        `${this.$store.state.apiUrl}products/relatorioOCP`
      );

      exportFromJSON({
        data: response.data.data,
        fileName: `Relatorio_OCP_${date_i}`,
        exportType: exportFromJSON.types.csv,
      });
    },

    async getProductSearch() {
      const saved = JSON.parse(localStorage.getItem('product'));
      if (saved != null) {
        this.q = String(saved);
        this.appliedQ = this.q;
        await this.filterProducts();
        localStorage.removeItem('product');
      }
    },
  },

  async created() {
    await this.brands();
    await this.getProductSearch();
  },
};
</script>

<style scoped>
/* =========================
   FULL WIDTH + FULL HEIGHT PAGE
   ========================= */
.products-page {
  width: 100%;
  max-width: none;
  margin: 0;
  padding: 18px;
  font-family: 'Inter', system-ui, -apple-system, Segoe UI, Roboto,
    'Helvetica Neue', Arial, sans-serif;
  color: #1f2937;

  /* ✅ para o card de resultados ocupar o resto do ecrã */
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* =========================
   LOADING
   ========================= */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(255, 255, 255, 0.65);
  z-index: 99999;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* =========================
   HERO
   ========================= */
.hero {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.title {
  font-size: 28px;
  font-weight: 900;
  letter-spacing: -0.02em;
  margin: 0;
  color: #0f172a;
}

/* =========================
   TOPBAR (Search + Filters + Actions)
   ========================= */
.topbar {
  display: grid;
  grid-template-columns: 1fr 460px auto; /* search | filtros | ações */
  gap: 14px;
  align-items: center; /* ✅ não estica */
}

/* ---------- Search pill ---------- */
.search-wrap {
  height: 48px; /* ✅ altura fixa */
  min-width: 420px;
  width: 100%;

  display: flex;
  align-items: center;
  gap: 10px;

  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 0 12px 0 14px; /* ✅ sem padding vertical */
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.search-wrap:focus-within {
  border-color: #cbd5e1;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.06);
}

.icon {
  display: inline-flex;
  color: #64748b;
}

.search {
  flex: 1;
  height: 100%;
  line-height: 48px; /* ✅ centra texto */
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  margin: 0;
  font-size: 15px;
  color: #0f172a;
}

.search::placeholder {
  color: #94a3b8;
}

.search-btn {
  height: 34px; /* ✅ não estica */
  padding: 0 14px;
  border: none;
  background: #111827;
  color: #fff;
  font-weight: 900;
  font-size: 14px;
  border-radius: 999px;
  cursor: pointer;
  transition: transform 0.06s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.search-btn:hover {
  background: #0b1220;
  box-shadow: 0 4px 14px rgba(17, 24, 39, 0.18);
}

.search-btn:active {
  transform: translateY(1px);
}

/* ---------- Filters group ---------- */
.filters {
  display: grid;
  grid-template-columns: 1fr 200px; /* marca | stock */
  gap: 12px;
  align-items: center;
}

.filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.label {
  font-weight: 900;
  font-size: 12px;
  color: #475569;
}

/* select nativo (Stock) */
.input {
  height: 48px; /* ✅ igual ao search */
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  outline: none;
  background: #fff;
  font-weight: 800;
  font-size: 13px;
}

/* ---------- Primary actions ---------- */
.primary-actions {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  white-space: nowrap;
}

.btn-create,
.btn-outline {
  height: 48px; /* ✅ igual ao resto */
  border-radius: 12px;
  font-weight: 900;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  transition: transform 0.06s ease, box-shadow 0.2s ease, background 0.2s ease,
    border-color 0.2s ease;
}

.btn-create {
  border: 1px solid #0f172a;
  background: #0f172a;
  color: #fff;
}

.btn-create:hover {
  background: #0b1220;
  border-color: #0b1220;
  box-shadow: 0 8px 18px rgba(2, 8, 23, 0.15);
}

.btn-create:active {
  transform: translateY(1px);
}

.btn-outline {
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #0f172a;
}

.btn-outline:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(2, 8, 23, 0.12);
}

.plus {
  font-size: 16px;
  line-height: 1;
}

/* =========================
   ACTIONS BAR
   ========================= */
.actions-bar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.action-btn {
  border-radius: 12px;
  padding: 10px 14px;
  font-weight: 900;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid #e5e7eb;
  background: #fff;
  color: #0f172a;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: transform 0.06s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(2, 8, 23, 0.12);
  background: #f8fafc;
}

.action-btn.green {
  border-color: #10b981;
  background: #ecfdf5;
  color: #047857;
}

/* =========================
   RESULTS CARD (fills remaining height)
   ========================= */
.table-card {
  flex: 1 1 auto; /* ✅ ocupa o resto do ecrã */
  min-height: 420px;
  background: #fff;
  width: 100%;
  border: 1px solid #eef2f7;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(2, 8, 23, 0.04);

  /* ✅ não cortar dropdowns */
  overflow: visible;

  display: flex;
  flex-direction: column;
}

.table-head {
  padding: 14px 16px;
  border-bottom: 1px solid #eef2f7;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
  background: #fbfdff;
  flex: 0 0 auto;
}

.table-title {
  font-weight: 900;
  color: #0f172a;
}

.table-meta {
  font-size: 12px;
  color: #64748b;
}

.muted {
  opacity: 0.85;
}

/* ✅ o WRAP é que faz scroll (não o card) */
.table-wrap {
  flex: 1 1 auto;
  min-height: 0; /* ✅ crucial em flex para não cortar */
  width: 100%;
  padding: 10px 12px 14px 12px;
  overflow: auto; /* scroll vertical/horizontal */
}

/* força tabela a ter largura grande e permitir horizontal */
.table-wrap :deep(.table) {
  width: 100%;
  min-width: 1400px;
}

/* =========================
   TABLE HEADER + CELLS ALIGNMENT
   ========================= */
.thead-soft th {
  background: #f8fafc !important;
  color: #0f172a !important;
  border-bottom: 1px solid #eef2f7 !important;
  font-weight: 900;
  font-size: 13px;
  vertical-align: top !important; /* ✅ header top */
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

/* ✅ td top para os dots ficarem alinhados */
:deep(td) {
  vertical-align: top !important;
}

:deep(.tbody-row td) {
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

/* =========================
   CONTENT STYLES
   ========================= */
.link {
  font-weight: 900;
  color: #0f172a;
  text-decoration: none;
}
.link:hover {
  text-decoration: underline;
}

.sub {
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
  font-size: 12px;
}

/* =========================
   STANDARD CELL LAYOUT (value + dot aligned top)
   ========================= */
.cell {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.cell-top {
  display: inline-flex;
  align-items: flex-start; /* ✅ top */
  gap: 8px;
  line-height: 1.1;
}

.cell-value {
  font-weight: 900;
  font-size: 13px;
  color: #0f172a;
}

.cell-sub {
  color: #64748b;
}

/* Dot aligned to top line */
.dot {
  height: 10px;
  width: 10px;
  margin-top: 2px; /* ✅ cola ao topo */
  border-radius: 999px;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.06);
}
.dot.red {
  background: #ef4444;
}
.dot.yellow {
  background: #f59e0b;
}
.dot.green {
  background: #10b981;
}

/* Info cell */
.info {
  font-size: 12px;
  color: #334155;
  display: grid;
  gap: 4px;
}

/* =========================
   IMAGE THUMB (zoom + modal)
   ========================= */
.thumb-wrap {
  position: relative;
  width: 76px;
  height: 76px;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  cursor: zoom-in;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-thumb {
  width: 72px;
  height: 72px;
  object-fit: contain;
  transition: transform 0.18s ease;
}

.thumb-wrap:hover .product-thumb {
  transform: scale(1.35);
}

.zoom-badge {
  position: absolute;
  right: 6px;
  bottom: 6px;
  font-size: 11px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 999px;
  padding: 2px 6px;
}

/* =========================
   VUE-SELECT (Marca + stock_status)
   ========================= */
/* dropdown acima de tudo */
.vs__dropdown-menu {
  z-index: 100000 !important;
}

/* Marca: altura 48px */
.vs :deep(.vs__dropdown-toggle) {
  height: 48px;
  border-radius: 12px;
  border-color: #e5e7eb;
}
.vs :deep(.vs__selected-options) {
  height: 48px;
  display: flex;
  align-items: center;
}
.vs :deep(.vs__search) {
  height: 48px;
  display: flex;
  align-items: center;
}

/* Stock status dentro da tabela */
.vs-mini :deep(.vs__dropdown-toggle) {
  border-radius: 12px;
  min-height: 34px;
  border-color: #e5e7eb;
}

/* =========================
   IMAGE MODAL
   ========================= */
.img-modal {
  display: grid;
  gap: 10px;
}
.img-modal-title {
  font-weight: 900;
  color: #0f172a;
}
.img-modal-img {
  width: 100%;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 14px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  padding: 10px;
}

/* =========================
   RESPONSIVE
   ========================= */
@media (max-width: 1200px) {
  .topbar {
    grid-template-columns: 1fr;
    gap: 12px;
  }
  .filters {
    grid-template-columns: 1fr 1fr;
  }
  .primary-actions {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
  .search-wrap {
    min-width: 0;
  }
}

@media (max-width: 640px) {
  .filters {
    grid-template-columns: 1fr;
  }
}
</style>
